import { useCallback, useEffect, useRef, useState } from "react";
import { fetchCandidates } from "../services/recruitmentService";

export const candidateStatusOptions = [
  { value: "REGISTERED", label: "Registered", color: "secondary" },
  { value: "SCREENING", label: "Screening", color: "info" },
  { value: "SELECTED", label: "Selected", color: "primary" },
  { value: "REJECTED", label: "Rejected", color: "danger" },
  { value: "DOCUMENTS_SENT", label: "Documents Sent", color: "warning" },
  { value: "OFFER_ACCEPTED", label: "Offer Accepted", color: "success" },
  { value: "APPOINTMENT", label: "Appointment", color: "dark" },
  { value: "HIRED", label: "Hired", color: "success" },
];

const defaultFilters = {
  search: "",
  status: null,
  page: 1,
  pageSize: 10,
};

const useRecruitmentCandidates = (initialFilters = {}) => {
  const mergedFilters = { ...defaultFilters, ...initialFilters };
  const filtersRef = useRef(mergedFilters);
  const [filters, setFilters] = useState(mergedFilters);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const loadCandidates = useCallback(async (override = {}) => {
    const nextFilters = {
      ...filtersRef.current,
      ...override,
    };
    setLoading(true);
    try {
      const response = await fetchCandidates({
        search: nextFilters.search,
        status: nextFilters.status,
        page: nextFilters.page,
        pageSize: nextFilters.pageSize,
      });

      const dataset = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.result)
        ? response.result
        : Array.isArray(response)
        ? response
        : [];

      setCandidates(dataset);
      setTotal(
        response?.meta?.total ||
          response?.total ||
          response?.count ||
          dataset.length ||
          0
      );
      filtersRef.current = nextFilters;
      setFilters(nextFilters);
    } catch (error) {
      console.error("Error fetching candidates:", error);
      setCandidates([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = useCallback(() => {
    loadCandidates();
  }, [loadCandidates]);

  const updateFilters = useCallback(
    (updated, resetPage = true) => {
      loadCandidates({
        ...updated,
        page: resetPage ? 1 : updated?.page ?? filtersRef.current.page,
      });
    },
    [loadCandidates]
  );

  const updatePagination = useCallback(
    (page, pageSize) => {
      loadCandidates({ page, pageSize });
    },
    [loadCandidates]
  );

  return {
    candidates,
    loading,
    filters,
    total,
    refresh,
    updateFilters,
    updatePagination,
  };
};

export default useRecruitmentCandidates;
